import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  Users,
  Clock,
  Shield,
  Zap,
  Building2,
  Code,
  Database,
  Cloud,
  Server,
  Lock,
  Network,
  BarChart3,
  Cpu,
  ChevronRight,
  Phone,
  Mail,
  Star,
  Target,
  TrendingUp,
  Briefcase,
  Award,
  Layers,
  GitBranch,
  Settings
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main technology categories
const technologies = [
  { name: "Java Development", icon: Code, description: "Lead Java, Sr. Java Developer, Java Backend, AWS/Kafka" },
  { name: "Data Analytics", icon: BarChart3, description: "Data Analyst, BI Analyst, Tableau, SQL/BI Developer" },
  { name: "Salesforce", icon: Cloud, description: "Sr. Salesforce Architect, Manager, Admin, Developer" },
  { name: "DevOps & SRE", icon: Server, description: "DevOps/AWS, SRE, Production Support, CI/CD" },
  { name: ".NET Development", icon: Cpu, description: ".Net Lead, Architect, AWS/GCP Integration" },
  { name: "Cloud & Data Engineering", icon: Cloud, description: "Data Engineer, Azure, AWS, GCP Specialists" },
  { name: "Business Intelligence", icon: BarChart3, description: "Tableau Developer, Power BI, SAS Developer" },
  { name: "AI & Data Science", icon: Database, description: "AI Product Manager, Data Science, ML Engineers" },
];

// All available roles for "View All" section
const allRoles = [
  "Lead Java Developer",
  "Sr Java Developer/AWS/Kafka",
  "Sr. Java Developer",
  "Sr. Salesforce Architect/Manager",
  "WMS/Manhattan",
  "Java Backend",
  ".Net Lead/Architect/AWS/GCP",
  "Data Conversion Lead/Data Analyst",
  "SAS Developer",
  "BI Analyst/Tableau",
  "SAS Developer/Data Analyst",
  "Ab Initio",
  "Tableau Developer",
  "SQL/BI Developer",
  "AI Product Manager",
  "Data Engineer/Azure",
  "Data Science",
  "ServiceNow Developer",
  "SRE/Production Support",
  "DevOps/AWS",
  "Cyber Security Analyst",
  "Network Engineer",
  "Cloud Architect",
  "Python Developer",
  "React Developer",
  "Node.js Developer"
];

const stats = [
  { number: "500+", label: "IT Professionals Deployed", icon: Users },
  { number: "50+", label: "Enterprise Clients", icon: Building2 },
  { number: "48hrs", label: "Average Deployment Time", icon: Clock },
  { number: "95%", label: "Client Retention Rate", icon: TrendingUp },
];

const benefits = [
  {
    icon: Zap,
    title: "Rapid Deployment",
    description: "Get skilled IT professionals onboarded within 48-72 hours. No lengthy hiring processes."
  },
  {
    icon: Shield,
    title: "Pre-Vetted Talent",
    description: "All candidates are technically assessed, background verified, and interview-ready."
  },
  {
    icon: Target,
    title: "Exact Skill Match",
    description: "We match your JD requirements with precision. Get candidates who fit your tech stack."
  },
  {
    icon: Award,
    title: "Quality Guaranteed",
    description: "Free replacement within 30 days if the resource doesn't meet your expectations."
  },
];

const testimonials = [
  {
    quote: "MC Talent Solutions provided us 15 Java developers within a week. Their screening process is thorough and saved us months of hiring effort.",
    author: "Rajesh Kumar",
    role: "CTO, Fintech Startup",
    company: "Mumbai"
  },
  {
    quote: "We've been partnering with them for our DevOps and Cloud requirements. Consistent quality and professional service.",
    author: "Priya Sharma",
    role: "VP Engineering",
    company: "IT Services Company, Pune"
  },
  {
    quote: "Their IT staffing solutions helped us scale our team from 20 to 100+ engineers in just 6 months.",
    author: "Amit Patel",
    role: "HR Director",
    company: "Enterprise Software Company"
  },
];

const OutsourcingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    requirements: "",
    positions: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        message: `🏢 IT STAFFING ENQUIRY

Company: ${formData.companyName}
Contact: ${formData.contactPerson}
Email: ${formData.email}
Phone: ${formData.phone}
Positions Required: ${formData.positions}

Requirements:
${formData.requirements}`
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setIsModalOpen(false);
        setFormData({
          companyName: "",
          contactPerson: "",
          email: "",
          phone: "",
          requirements: "",
          positions: ""
        });
      }, 2500);
    } catch (err) {
      console.error("Error:", err);
      alert("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
              <img src="/logo.png" alt="MC Talent Solutions" className="h-14 w-auto" />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate("/")} className="text-gray-600 hover:text-gray-900 font-medium">
                Home
              </button>
              <button onClick={() => navigate("/jobs")} className="text-gray-600 hover:text-gray-900 font-medium">
                Current Openings
              </button>
              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6"
              >
                Get IT Talent
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-600 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full">
                <Briefcase className="h-4 w-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">IT Manpower Outsourcing</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Hire Experienced IT Talent.{" "}
                <span className="text-orange-500">Faster. Smarter.</span>
              </h1>

              <p className="text-xl text-gray-300 max-w-xl">
                Access pre-vetted, interview-ready IT professionals for your projects. 
                From Java developers to Cloud architects — we deliver talent that delivers results.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-orange-500/25 transition-all hover:scale-105"
                >
                  Request IT Talent
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => document.getElementById('technologies').scrollIntoView({ behavior: 'smooth' })}
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                >
                  View Technologies
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 border-2 border-gray-900 flex items-center justify-center text-white text-xs font-bold">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div className="text-sm text-gray-400">
                  <span className="text-white font-semibold">50+ Companies</span> trust us for IT staffing
                </div>
              </div>
            </div>

            {/* Stats Card */}
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center p-4">
                      <stat.icon className="h-8 w-8 text-orange-400 mx-auto mb-2" />
                      <div className="text-3xl font-bold text-white">{stat.number}</div>
                      <div className="text-sm text-gray-400">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              IT Technologies We <span className="text-orange-500">Staff For</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From legacy systems to cutting-edge technologies, we provide skilled professionals across the IT spectrum
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technologies.map((tech, index) => (
              <Card 
                key={index} 
                className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white cursor-pointer"
                onClick={() => setIsModalOpen(true)}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform">
                    <tech.icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{tech.name}</h3>
                  <p className="text-gray-600 text-sm">{tech.description}</p>
                  <div className="mt-4 flex items-center text-orange-500 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Request Talent <ChevronRight className="h-4 w-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* All Available Roles */}
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Currently Hiring For</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {allRoles.map((role, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 bg-gray-100 hover:bg-orange-100 hover:text-orange-600 rounded-full text-sm font-medium text-gray-700 cursor-pointer transition-colors"
                  onClick={() => setIsModalOpen(true)}
                >
                  {role}
                </span>
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8"
            >
              Request Any IT Talent
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Companies Choose <span className="text-orange-500">MC Talent</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're not just another staffing agency. We're your strategic IT talent partner.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 mx-auto rounded-full bg-orange-100 flex items-center justify-center text-orange-500 mb-4 group-hover:bg-orange-500 group-hover:text-white transition-all">
                  <benefit.icon className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Simple <span className="text-orange-400">3-Step</span> Process
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From requirement to deployment in as little as 48 hours
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Share Requirements", description: "Tell us your tech stack, experience level, and team size needed" },
              { step: "02", title: "Review Profiles", description: "We send pre-screened, interview-ready candidates within 24-48 hours" },
              { step: "03", title: "Onboard & Scale", description: "Select candidates and we handle all onboarding formalities" },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-orange-500/20 absolute -top-4 left-0">{item.step}</div>
                <div className="pt-12 pl-4">
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-8 w-8 text-orange-500/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by <span className="text-orange-500">Industry Leaders</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="h-5 w-5 fill-orange-400 text-orange-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold">
                      {testimonial.author.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.author}</div>
                      <div className="text-sm text-gray-500">{testimonial.role}</div>
                      <div className="text-xs text-gray-400">{testimonial.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build Your Dream IT Team?
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Get access to 500+ pre-vetted IT professionals. No upfront costs. Pay only when you hire.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-white text-orange-600 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
            >
              Request IT Talent Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
              onClick={() => window.location.href = 'tel:+919350364233'}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call: +91 93503 64233
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Bar */}
      <section className="py-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center items-center gap-8">
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-orange-400" />
            <span>+91 93503 64233</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-orange-400" />
            <span>hiring@mctalentsolutionsai.com</span>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-orange-400" />
            <span>Gurugram: 10th Floor, Tower-B, Unitech Cyber Park, Sector 39 – 122001 &nbsp;|&nbsp; Pune: Regus, 4th Floor, Prabhavee Tech Park, Baner – 411045</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-black text-gray-400 text-center text-sm">
        <p>© 2026 MC Talent Solutions Pvt. Ltd. All rights reserved.</p>
      </footer>

      {/* Enquiry Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-orange-500" />
              Request IT Talent
            </DialogTitle>
          </DialogHeader>

          {success ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900">Thank You!</h3>
              <p className="text-gray-600 mt-2">Our team will contact you within 24 hours with matching profiles.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    placeholder="Your company"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactPerson">Contact Person *</Label>
                  <Input
                    id="contactPerson"
                    placeholder="Your name"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Work Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="work@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="positions">Number of Positions</Label>
                <Input
                  id="positions"
                  placeholder="e.g., 5 Java Developers, 2 DevOps Engineers"
                  value={formData.positions}
                  onChange={(e) => setFormData({ ...formData, positions: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="requirements">Requirements / Tech Stack *</Label>
                <Textarea
                  id="requirements"
                  placeholder="Describe your requirements: technologies, experience level, project duration, etc."
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  rows={4}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg rounded-full"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Requirement"}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                We'll respond within 24 hours with matching candidate profiles
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OutsourcingPage;
